import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { fontSizeLabelMedium } from '@calendly/ui/theme'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import ErrorMessage from 'components/core/error_message'
import Settings from 'core/settings'
import { STRIPE_API_VERSION, checkoutSelectedCurrencySelector, checkoutEstimateSelector } from 'features/billing'
import { useAssetUrl } from 'hooks/useAssetUrl'

const Container = ({ isPaypalAvailable, stripeAccount, mode, children }) => {
  const [error, setError] = useState(null)
  const stripeMigrationEnabled = useFlag('stripe_create_webapp_subscription_rollout')
  const stripePaypalByopEnabled = useFlag('stripe_paypal_byop_rollout')
  const currency = useSelector(checkoutSelectedCurrencySelector)

  const estimate = useSelector(checkoutEstimateSelector)

  const stripeOptions = useMemo(() => ({
    stripeAccount
  }), [stripeAccount])

  if (stripeMigrationEnabled) {
    stripeOptions.apiVersion = STRIPE_API_VERSION
  }
  const stripePromise = useMemo(() => loadStripe(Settings.stripe.publishable_key, stripeOptions), [stripeOptions])
  const fontUrl = useAssetUrl('external/proxima-nova.css')

  useEffect(() => {
    async function awaitStripePromise() {
      try {
        await stripePromise
        setError(null)
      } catch (e) {
        setError(I18n.t('errors.billing.card.payment_form_not_loaded'))
      }
    }
    awaitStripePromise()
  }, [stripePromise])

  if (error) {
    return <ErrorMessage message={error} />
  }

  const options = {
    locale: I18n.locale,
    fonts: [{ cssSrc: fontUrl }],
  }
  if (stripeMigrationEnabled) {
    options.mode = mode ?? 'subscription'
    options.amount = estimate?.total || 0
    options.currency = currency?.toLowerCase() || 'usd'
    options.paymentMethodCreation = 'manual'
    if (Settings.stripe.payment_method_configuration) {
      options.payment_method_configuration = Settings.stripe.payment_method_configuration
    }

    // Restore PayPal custom payment method configuration when stripe_paypal_byop_rollout is enabled (Payment Session flow)
    if (stripePaypalByopEnabled && isPaypalAvailable && Settings.stripe.paypal_custom_payment_id) {
      // Merge PayPal custom payment method with existing ones instead of overwriting
      const paypalPaymentMethod = {
        id: Settings.stripe.paypal_custom_payment_id,
        options: {
          type: 'static'
        }
      }

      // Check if PayPal payment method already exists to avoid duplicates
      const existingPaymentMethods = options.customPaymentMethods || []
      const paypalExists = existingPaymentMethods.some(
        (method) => method.id === Settings.stripe.paypal_custom_payment_id
      )

      if (!paypalExists) {
        // Merge PayPal payment method with existing ones using spread operator
        options.customPaymentMethods = [...existingPaymentMethods, paypalPaymentMethod]
      }
      // If PayPal already exists, no action needed - existingPaymentMethods already contains it
    }

    options.appearance = {
      theme: 'stripe',
      variables: {
        colorText: '#0a2540', // colorTextStandard
      },
      rules: {
        '.Label': {
          fontSize: fontSizeLabelMedium,
          fontFamily: '"Proxima Nova", sans-serif',
          fontWeight: '700',
          lineHeight: '1.4',
        },
      },
    }
  }
  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  )
}

export default Container
